import { Component, OnInit } from '@angular/core';

import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, Subject, take, pluck, map, takeUntil } from 'rxjs';

import { NotificationLayout } from '@fcom/ui-components';
import { StorageService, AppState } from '@fcom/core';
import { ProfileTier, ProfileType } from '@fcom/core-api/login';
import { profile, profileType } from '@fcom/core/selectors';
import { LocalizationPipe } from '@fcom/ui-translate/pipes/localization.pipe';

export const LOYALTY_GLOBAL_NOTIFICATION_STORAGE_KEY = 'loyalty-global-notification-closed';

@Component({
  selector: 'fin-loyalty-global-notification',
  templateUrl: './loyalty-global-notification.component.html',
  standalone: false,
})
export class LoyaltyGlobalNotificationComponent implements OnInit {
  private destroy$ = new Subject<boolean>();

  readonly NotificationLayout = NotificationLayout;
  readonly ProfileTier = ProfileTier;
  readonly ProfileType = ProfileType;

  showNotification$ = new BehaviorSubject<boolean>(false);
  profileTier$: Observable<ProfileTier>;
  profileType$: Observable<ProfileType>;
  notificationData$: Observable<{ hidden: boolean; message?: string }>;

  constructor(
    private store$: Store<AppState>,
    private storageService: StorageService,
    private finLocalization: LocalizationPipe
  ) {
    this.profileTier$ = this.store$.pipe(profile(), pluck('tier'), takeUntil(this.destroy$));
    this.profileType$ = this.store$.pipe(profileType(), take(1));

    this.notificationData$ = combineLatest([this.profileTier$, this.showNotification$]).pipe(
      map(([tier, showNotification]) => {
        let isInitiallyHidden = 'true';
        let message = '';

        switch (tier) {
          case ProfileTier.BASIC:
          case ProfileTier.JUNIOR:
            isInitiallyHidden = this.finLocalization.transform('LOYALTY.notification.global.generalTier.hidden');
            message = this.finLocalization.transform('LOYALTY.notification.global.generalTier.message');
            break;
          case ProfileTier.SILVER:
            isInitiallyHidden = this.finLocalization.transform('LOYALTY.notification.global.silverTier.hidden');
            message = this.finLocalization.transform('LOYALTY.notification.global.silverTier.message');
            break;
          case ProfileTier.GOLD:
            isInitiallyHidden = this.finLocalization.transform('LOYALTY.notification.global.goldTier.hidden');
            message = this.finLocalization.transform('LOYALTY.notification.global.goldTier.message');
            break;
          case ProfileTier.PLATINUM:
            isInitiallyHidden = this.finLocalization.transform('LOYALTY.notification.global.platinumTier.hidden');
            message = this.finLocalization.transform('LOYALTY.notification.global.platinumTier.message');
            break;
          case ProfileTier.LUMO:
            isInitiallyHidden = this.finLocalization.transform('LOYALTY.notification.global.platinumLumoTier.hidden');
            message = this.finLocalization.transform('LOYALTY.notification.global.platinumLumoTier.message');
            break;
        }

        return { hidden: !showNotification || isInitiallyHidden !== 'false' || !message, message };
      }),
      takeUntil(this.destroy$)
    );
  }

  ngOnInit() {
    this.showNotification$.next(
      (this.storageService.LOCAL.get(LOYALTY_GLOBAL_NOTIFICATION_STORAGE_KEY) || 'false') === 'false'
    );
  }

  onCloseNotification() {
    this.storageService.LOCAL.set(LOYALTY_GLOBAL_NOTIFICATION_STORAGE_KEY, true);
    this.showNotification$.next(false);
  }

  onDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
